<template>
  <div class="retinoscopy-fields w-full flex flex-col gap-8" dir="ltr">
    <!-- Retinoscopy optical crosses (dry / cycloplegic) -->
    <section v-for="section in retinoscopySections" :key="section.key">
      <div class="section-title retinoscopy-title">
        <div>
          <div class="font-bold">{{ en(section.title) }}<bdi v-if="isArabic" class="title-ar">{{ t(section.title) }}</bdi></div>
          <div class="text-xs text-gray-500"><bdi>{{ t(section.subtitle) }}</bdi></div>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-sm font-bold">
            <bdi>{{ t('workingDistance') }}</bdi><span v-if="retinoscopyHasValues(modelValue[section.key])" class="wd-required">*</span>
          </span>
          <el-select
              :model-value="sectionWorkingDistance(section.key)"
              :class="{ 'wd-missing': retinoscopyMissingWorkingDistance(modelValue[section.key]) }"
              clearable
              placeholder=" "
              style="width: 150px"
              @update:model-value="(wd: unknown) => setSectionWorkingDistance(section.key, wd)"
          >
            <el-option
                v-for="wd in workingDistances"
                :key="wd"
                :label="workingDistanceLabel(wd)"
                :value="wd"
            />
          </el-select>
          <div class="cyl-toggle" dir="ltr">
            <button
                v-for="notation in cylNotations" :key="notation" type="button"
                :class="{active: rxNotation(section.key) === notation}"
                @click="model[section.key].cylNotation = notation"
            >{{ notation === 'plus' ? '+cyl' : '-cyl' }}</button>
          </div>
        </div>
      </div>
      <div class="cross-row">
        <div
            v-for="(eye, index) in eyes"
            :key="eye"
            class="cross-wrapper"
            :class="{ 'cross-divider': index > 0 }"
        >
          <div class="cross-eye-label">
            <span class="font-bold">{{ eye.toUpperCase() }}</span>
            <span class="text-xs text-gray-500 mx-1">{{ en(eye === 'od' ? 'rightEye' : 'leftEye') }}</span>
          </div>
          <div class="cross">
            <div class="cross-axis-v"></div>
            <div class="cross-axis-h"></div>
            <label
                v-for="(position, index) in axisPositions"
                :key="position"
                class="cross-axis-box"
                :class="`cross-axis-${position}`"
                :title="t('axisHint')"
            >
              <input
                  :value="retinoscopyAxes(model[section.key][eye])[index]"
                  type="text"
                  inputmode="numeric"
                  maxlength="3"
                  @focus="($event.target as HTMLInputElement).select()"
                  @change="setAxis(model[section.key][eye], index, $event)"
              />°
            </label>
            <svg class="cross-dial" viewBox="0 0 48 48" aria-hidden="true">
              <title>{{ t('dialHint') }}</title>
              <circle cx="24" cy="24" r="22" class="cross-dial-ring"/>
              <line
                  v-for="angle in retinoscopyAxes(model[section.key][eye])"
                  :key="angle"
                  v-bind="dialLine(angle)"
                  class="cross-dial-line"
              />
              <circle cx="24" cy="24" r="2" fill="#1e4f7b"/>
            </svg>

            <div
                v-for="meridian in meridians"
                :key="meridian"
                class="cross-meridian"
                :class="`cross-${meridian}`"
            >
              <span class="meridian-caption">{{ en('gross') }}</span>
              <CustomNumberInput
                  v-model="model[section.key][eye][meridian].gross"
                  :step="0.25"
                  :precision="2"
                  :min="-20"
                  :max="20"
                  signed
                  clamp
              />
              <div class="col-span-2 border-t border-gray-400 my-0.5"></div>
              <span class="meridian-caption font-bold">{{ en('net') }}</span>
              <div class="net-box" :title="t('netHint')">
                {{ formatDiopter(model[section.key][eye][meridian].net) }}
              </div>
            </div>
          </div>
          <div class="cross-rx">
            <span class="meridian-caption">{{ en('rx') }}</span>
            <span :title="t(rxNotation(section.key) === 'plus' ? 'rxHintPlus' : 'rxHint')" class="flex items-center">
              <span v-if="formatRx(retinoscopyRx(model[section.key][eye], rxNotation(section.key)))" class="cross-rx-value">{{ formatRx(retinoscopyRx(model[section.key][eye], rxNotation(section.key))) }}</span>
              <span v-else class="font-bold">—</span>
            </span>
          </div>
        </div>
      </div>
      <div class="text-xs text-gray-500 mt-3"><bdi>{{ t('axisHint') }}</bdi></div>
    </section>

    <section v-if="showNotes">
      <div class="section-title">
        <div class="font-bold">{{ en('notes') }}<bdi v-if="isArabic" class="title-ar">{{ t('notes') }}</bdi></div>
      </div>
      <el-input
          v-model="model.notes"
          class="retinoscopy-notes"
          :autosize="{ minRows: 3, maxRows: 10 }"
          type="textarea"
          placeholder=""
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import {computed, watch, type PropType} from 'vue'
import {useI18n} from 'vue-i18n'
import CustomNumberInput from '@/views/examinations/CustomNumberInput.vue'
import {useSettingsStore} from '@/stores/settingsStore'
import {
  CYL_NOTATION_SETTING_KEY,
  RETINOSCOPY_WORKING_DISTANCES,
  cylNotationFromSetting,
  formatDiopter,
  formatRx,
  normalizeRetinoscopyAxis,
  retinoscopyAxes,
  retinoscopyHasValues,
  retinoscopyMissingWorkingDistance,
  retinoscopyNet,
  retinoscopyRx,
  retinoscopyWorkingDistances,
  sectionCylNotation,
  setRetinoscopyWorkingDistance,
  workingDistanceLabel,
  type Retinoscopy,
  type RetinoscopyCross,
  type RetinoscopyEyes
} from '@/models/customer/Examination'

const props = defineProps({
  modelValue: {
    type: Object as PropType<Retinoscopy>,
    required: true
  },
  // Old stand-alone Retinoscopy exams keep their notes in the exam's own Notes field
  showNotes: {
    type: Boolean,
    default: true
  }
})

const {t, locale} = useI18n({
  useScope: 'local',
  inheritLocale: true,
  messages: {
    en: {
      dryRetinoscopy: 'Dry Retinoscopy',
      dryRetinoscopySubtitle: 'Retinoscopy without cycloplegic drops',
      cycloplegicRetinoscopy: 'Cycloplegic Retinoscopy',
      cycloplegicRetinoscopySubtitle: 'Retinoscopy after cycloplegic drops',
      rightEye: 'Right eye',
      leftEye: 'Left eye',
      gross: 'Gross',
      workingDistance: 'Working distance (WD)',
      net: 'Net',
      netHint: 'Net = Gross + WD (calculated automatically)',
      notes: 'Retinoscopy Notes',
      rx: 'Rx',
      rxHint: 'Minus-cylinder prescription calculated from the Net values',
      rxHintPlus: 'Plus-cylinder prescription calculated from the Net values',
      axisHint: 'Type either axis and the other one updates automatically, always 90° apart',
      dialHint: 'Real orientation of the two meridians (TABO)'
    },
    ar: {
      dryRetinoscopy: 'تنظير الشبكية بدون قطرة',
      dryRetinoscopySubtitle: 'فحص الريتينوسكوب بدون قطرة التوسيع',
      cycloplegicRetinoscopy: 'تنظير الشبكية بعد القطرة',
      cycloplegicRetinoscopySubtitle: 'فحص الريتينوسكوب بعد قطرة التوسيع',
      rightEye: 'العين اليمنى',
      leftEye: 'العين اليسرى',
      gross: 'Gross',
      workingDistance: 'مسافة العمل (WD)',
      net: 'Net',
      netHint: 'Net = Gross + WD (تُحسب تلقائياً)',
      notes: 'ملاحظات الـ Retinoscopy',
      rx: 'Rx',
      rxHint: 'وصفة بأسطوانة سالبة تُحسب من قيم Net',
      rxHintPlus: 'وصفة بأسطوانة موجبة تُحسب من قيم Net',
      axisHint: 'اكتب أحد المحورين والآخر يتحدّث تلقائياً، والفرق بينهما دائماً 90 درجة',
      dialHint: 'الاتجاه الحقيقي للمحورين (TABO)'
    }
  }
})

// The form is always LTR; in Arabic, titles show English plus Arabic
const isArabic = computed(() => locale.value === 'ar')
const en = (key: string) => t(key, {}, {locale: 'en'})

const eyes = ['od', 'os'] as const
const meridians = ['meridian90', 'meridian180'] as const
const workingDistances = RETINOSCOPY_WORKING_DISTANCES
const axisPositions = ['top', 'bottom'] as const

// Index 0 is meridian90's axis, index 1 meridian180's; the other one always sits 90° away
function setAxis(cross: RetinoscopyCross, index: number, event: Event) {
  const input = event.target as HTMLInputElement
  const axis = normalizeRetinoscopyAxis(input.value)
  if (axis != null) cross.axis = index === 0 ? axis : normalizeRetinoscopyAxis(axis + 90)
  input.value = String(retinoscopyAxes(cross)[index])
}

// TABO: 0° to the right, counter-clockwise
function dialLine(angle: number) {
  const x = 19 * Math.cos(angle * Math.PI / 180)
  const y = 19 * Math.sin(angle * Math.PI / 180)
  return {x1: 24 - x, y1: 24 + y, x2: 24 + x, y2: 24 - y}
}

const retinoscopySections = [
  {key: 'dryRetinoscopy', title: 'dryRetinoscopy', subtitle: 'dryRetinoscopySubtitle'},
  {key: 'cycloplegicRetinoscopy', title: 'cycloplegicRetinoscopy', subtitle: 'cycloplegicRetinoscopySubtitle'}
] as const

type RetinoscopySectionKey = typeof retinoscopySections[number]['key']

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const model = computed(() => props.modelValue as any)
const settingsStore = useSettingsStore()
const cylNotation = computed(() => cylNotationFromSetting(settingsStore.getValue(CYL_NOTATION_SETTING_KEY)))
const cylNotations = ['plus', 'minus'] as const

function rxNotation(key: RetinoscopySectionKey) {
  return sectionCylNotation(props.modelValue[key], cylNotation.value)
}

function sectionWorkingDistance(key: RetinoscopySectionKey): number | null {
  const values = retinoscopyWorkingDistances(props.modelValue[key])
  return values.length === 1 ? values[0] : null
}

function setSectionWorkingDistance(key: RetinoscopySectionKey, wd: unknown) {
  setRetinoscopyWorkingDistance(props.modelValue[key], wd === '' || wd == null ? null : Number(wd))
}

watch(
    () => props.modelValue,
    (value) => {
      for (const section of retinoscopySections) {
        const crosses: RetinoscopyEyes = value[section.key]
        for (const eye of eyes) {
          for (const meridian of meridians) {
            const m = crosses[eye][meridian]
            const net = retinoscopyNet(m)
            if (m.net !== net) m.net = net
          }
        }
      }
    },
    {deep: true, immediate: true}
)
</script>

<style scoped>
.section-title {
  @apply mb-3;
}

.title-ar {
  margin-left: 0.75rem;
}

.cross-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 420px));
}

.cross-wrapper {
  @apply flex flex-col px-6;
  min-width: 0;
}

.cross-wrapper:first-child {
  padding-left: 0;
}

.cross-divider {
  border-left: 1px solid #9b9b9b;
}

.cross-eye-label {
  @apply mb-2;
}

.cross {
  position: relative;
  width: 100%;
  max-width: 380px;
  min-width: 330px;
  height: 210px;
}

.cross-axis-v {
  position: absolute;
  left: 44px;
  top: 0;
  bottom: 0;
  border-left: 2px solid #000;
}

.cross-axis-h {
  position: absolute;
  left: 16px;
  right: 0;
  bottom: 30px;
  border-top: 2px solid #000;
}

.cross-axis-box {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 1px;
  font-weight: 700;
}

.cross-axis-box input {
  width: 36px;
  height: 24px;
  text-align: center;
  font-weight: 700;
  border: 1px solid #c0c4cc;
  border-radius: 4px;
  background: #fff;
}

.cross-axis-box input:focus {
  outline: none;
  border-color: #1e4f7b;
  box-shadow: 0 0 0 2px #dbecfc;
}

.cross-axis-top {
  left: 0;
  top: -2px;
}

.cross-axis-bottom {
  right: 0;
  bottom: 0;
}

.cross-dial {
  position: absolute;
  right: 4px;
  top: 0;
  width: 48px;
  height: 48px;
}

.cross-dial-ring {
  fill: #fff;
  stroke: #e5e7eb;
}

.cross-dial-line {
  stroke: #000;
  stroke-width: 2;
}

.cross-meridian {
  position: absolute;
  width: 160px;
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 4px 6px;
  align-items: center;
}

.cross-meridian90 {
  left: 54px;
  top: 4px;
}

.cross-meridian180 {
  right: 0;
  bottom: 38px;
}

.meridian-caption {
  font-size: 11px;
  color: #555;
  text-align: end;
  white-space: nowrap;
}

.retinoscopy-title {
  @apply flex flex-wrap items-end justify-between gap-4;
  max-width: 840px;
}

.net-box {
  height: 32px;
  line-height: 30px;
  text-align: center;
  font-weight: 700;
  color: #1e4f7b;
  background: #dbecfc;
  border: 1px solid #b6d4f2;
  border-radius: 4px;
}

.cross-rx {
  @apply flex items-center gap-2 mt-2;
  color: #1e4f7b;
}

/* Same labels in English and Arabic */
.cyl-toggle {
  @apply flex ms-2;
}

.wd-required {
  @apply ms-0.5;
  color: var(--el-color-danger);
}

.wd-missing :deep(.el-select__wrapper) {
  box-shadow: 0 0 0 1px var(--el-color-danger) inset;
}

.cyl-toggle button {
  @apply text-sm font-semibold px-3 py-1;
  color: #4b5563;
  background: #e5e7eb;
  border: 1px solid #d1d5db;
}

.cyl-toggle button:first-child {
  border-radius: 6px 0 0 6px;
}

.cyl-toggle button:last-child {
  border-radius: 0 6px 6px 0;
  border-left: 0;
}

.cyl-toggle button.active {
  color: #1e4f7b;
  background: #dbecfc;
  border-color: #b6d4f2;
}

/* The Rx is the result of the cross, so it sits in a box like the Net values */
.cross-rx-value {
  @apply font-bold text-lg px-3 py-1 rounded-lg;
  background: #dbecfc;
  border: 1px solid #b6d4f2;
}

.retinoscopy-notes :deep(textarea) {
  unicode-bidi: plaintext;
}
/* Phones only: desktop layout above is unchanged */
@media (max-width: 767px) {
  .cross-row {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 1.25rem;
  }

  .cross-wrapper {
    padding: 0;
  }

  .cross-divider {
    border-left: 0;
    border-top: 1px solid #9b9b9b;
    padding-top: 1rem;
  }

  .cross {
    min-width: 0;
    max-width: none;
  }

  /* Keep the Arabic title in one piece so it wraps as a block with its gap */
  .title-ar {
    display: inline-block;
  }
}
</style>
